import { Controller } from "@hotwired/stimulus"

/**
 * MediaBrowserSubmit Controller
 *
 * Handles form submission for attaching selected image to target resource.
 * Manages loading states, success/error handling, and modal closure.
 */
export default class extends Controller {
  static targets = ["submitButton", "errorContainer", "errorMessage", "buttonText"]
  static values = {
    resourceType: String,
    resourceId: String,
    attachmentName: String
  }

  connect() {
    // Controller initialized
  }

  /**
   * Submit selection to attach image to resource
   * Called when user clicks "Select Image" button
   */
  async selectImage(event) {
    event.preventDefault()

    // Get selected blob ID from selection controller
    const selectionController = this.application.getControllerForElementAndIdentifier(
      this.element,
      "media-browser-selection"
    )

    if (!selectionController) {
      this.showError("Unable to find selection controller")
      return
    }

    const selectedBlobId = selectionController.getSelectedBlobId()
    const selectedImageUrl = selectionController.getSelectedImageUrl()

    if (!selectedBlobId) {
      this.showError("Please select an image first")
      return
    }

    // Debug logging
    console.log("Media Browser Submit - Context Check:", {
      resourceType: this.resourceTypeValue,
      resourceId: this.resourceIdValue,
      attachmentName: this.attachmentNameValue,
      hasBlockType: !!window.mediaBrowserBlockType,
      blockType: window.mediaBrowserBlockType,
      blockId: window.mediaBrowserBlockId
    })

    // Check if this is a BlockNote selection (no resource context)
    const isBlockNoteSelection = !this.resourceTypeValue && window.mediaBrowserBlockType

    if (isBlockNoteSelection) {
      // For BlockNote, call backend to get properly-sized variant
      await this.handleBlockNoteSelection(selectedBlobId, window.mediaBrowserBlockType, window.mediaBrowserBlockId)
      return
    }

    // Validate we have all required context for resource attachment
    if (!this.resourceTypeValue || !this.resourceIdValue || !this.attachmentNameValue) {
      this.showError("Missing required resource context")
      console.error("Missing context:", {
        resourceType: this.resourceTypeValue,
        resourceId: this.resourceIdValue,
        attachmentName: this.attachmentNameValue,
        element: this.element,
        elementDataset: this.element?.dataset
      })
      return
    }

    // Show loading state
    this.setLoadingState(true)
    this.hideError()

    // Get CSRF token
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    if (!csrfToken) {
      this.showError("Security token not found. Please reload the page.")
      this.setLoadingState(false)
      return
    }

    try {
      // Submit to MediaBrowserController#select
      const response = await fetch("/media_browser/select", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          blob_id: selectedBlobId,
          resource_type: this.resourceTypeValue,
          resource_id: this.resourceIdValue,
          attachment_name: this.attachmentNameValue
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Success! Handle successful attachment
        this.handleSuccess(data)
      } else {
        // Server returned error
        const errorMessage = data.error || "Failed to attach image"
        this.showError(errorMessage)
        this.setLoadingState(false)
      }
    } catch (error) {
      console.error("Attachment failed:", error)
      this.showError("Network error. Please check your connection and try again.")
      this.setLoadingState(false)
    }
  }

  /**
   * Handle BlockNote image selection by calling backend for proper variant
   */
  async handleBlockNoteSelection(blobId, blockType, blockId) {
    // Show loading state
    this.setLoadingState(true)
    this.hideError()

    // Get CSRF token
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    if (!csrfToken) {
      this.showError("Security token not found. Please reload the page.")
      this.setLoadingState(false)
      return
    }

    try {
      // Call backend to get properly-sized variant for this block type
      const response = await fetch("/media_browser/select_for_blocknote", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          blob_id: blobId,
          block_type: blockType
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Success! Dispatch event with properly-sized variant URL and blob metadata
        this.handleBlockNoteSuccess(data.url, blockType, blockId, data.variants || {}, data.blob || null)
      } else {
        // Server returned error
        const errorMessage = data.error || "Failed to load image"
        this.showError(errorMessage)
        this.setLoadingState(false)
      }
    } catch (error) {
      console.error("BlockNote selection failed:", error)
      this.showError("Network error. Please check your connection and try again.")
      this.setLoadingState(false)
    }
  }

  /**
   * Handle successful BlockNote image selection
   * Dispatches mediaBrowserSelected event with image URL, variants, blob metadata, block type, and block ID
   */
  handleBlockNoteSuccess(imageUrl, blockType, blockId, variants = {}, blob = null) {
    console.log("BlockNote image selected:", imageUrl, "for block type:", blockType, "block ID:", blockId)

    // Dispatch new CustomEvent-based event with blockId for unique identification
    document.dispatchEvent(new CustomEvent("mediaBrowserSelected", {
      detail: {
        imageUrl,
        variants,
        blob,  // Include blob metadata (signed_id, filename, content_type)
        blockType,
        blockId  // Include block ID so only the correct instance responds
      }
    }))

    // Close the modal
    this.closeModal()
  }

  /**
   * Handle successful image attachment
   */
  handleSuccess(data) {
    console.log("Image attached successfully:", data)

    // Dispatch custom event for other controllers to handle
    this.element.dispatchEvent(new CustomEvent("media-browser:attached", {
      bubbles: true,
      detail: {
        url: data.url,
        variantsGenerated: data.variants_generated,
        resourceType: this.resourceTypeValue,
        resourceId: this.resourceIdValue,
        attachmentName: this.attachmentNameValue
      }
    }))

    // Close the modal
    this.closeModal()

    // Reload the page to show updated image
    // TODO: In future, use Turbo Stream to update specific element without full reload
    setTimeout(() => {
      window.location.reload()
    }, 300)
  }

  /**
   * Set loading state on submit button
   */
  setLoadingState(isLoading) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = isLoading

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = isLoading ? "Attaching..." : "Select Image"
    }

    // Add/remove loading spinner
    if (isLoading) {
      const spinner = `
        <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      `
      this.submitButtonTarget.insertAdjacentHTML("afterbegin", spinner)
    } else {
      const spinner = this.submitButtonTarget.querySelector("svg.animate-spin")
      if (spinner) spinner.remove()
    }
  }

  /**
   * Show error message to user
   */
  showError(message) {
    if (!this.hasErrorContainerTarget || !this.hasErrorMessageTarget) return

    this.errorMessageTarget.textContent = message
    this.errorContainerTarget.classList.remove("hidden")

    // Scroll error into view
    this.errorContainerTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })

    // Announce error to screen readers
    this.announceError(message)
  }

  /**
   * Hide error message
   */
  hideError() {
    if (!this.hasErrorContainerTarget) return
    this.errorContainerTarget.classList.add("hidden")
  }

  /**
   * Announce error to screen readers for accessibility
   */
  announceError(message) {
    let liveRegion = document.getElementById("media-browser-error-announcements")

    if (!liveRegion) {
      liveRegion = document.createElement("div")
      liveRegion.id = "media-browser-error-announcements"
      liveRegion.setAttribute("role", "alert")
      liveRegion.setAttribute("aria-live", "assertive")
      liveRegion.setAttribute("aria-atomic", "true")
      liveRegion.className = "sr-only"
      document.body.appendChild(liveRegion)
    }

    liveRegion.textContent = `Error: ${message}`
  }

  /**
   * Close the modal
   */
  closeModal() {
    // Dispatch modal close event
    this.element.dispatchEvent(new CustomEvent("modal:close", {
      bubbles: true
    }))
  }
}
